// apps/client/src/pages/reports/SettlementAgeReportPage.tsx
// MIGRATED TO BIGQUERY (2025-11-17): Query raw_commissions via callable function
// FROM: Firestore active_loans onSnapshot (real-time, expensive)
// TO: BigQuery getActiveLoansList (on-demand, cheaper, faster)

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection } from '../../utils/loanBookResolution';
import { useUserData } from '../../hooks/useUserData';
import { useSettlementAge } from '../../hooks/useSettlementAge';
import { db } from '../../lib/firebase';
import {
  collection,
  query,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { formatDollars, formatDollarsShorthand } from '../../utils/formatters';
import { Clock, ChevronDown, ChevronUp, Layers, ChevronRight } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { SkeletonReportPage } from '../../components/common/SkeletonLoader';
import { Spinner } from '../../components/common/Spinner';
import { useLoanContacts, LoanContactEntry } from '../../hooks/useLoanContacts';
import type { MutateContactInput } from '../../hooks/useLoanContacts';
import ContactTimeline from '../../components/contacts/ContactTimeline';

// Type definitions
// Client-contact state now lives in the canonical loan_contacts store via
// useLoanContacts + the shared <ContactTimeline> component.

interface FilterState {
  yearRangeStart: number;
  yearRangeEnd: number;
  brokerFilter: string;
  lenderFilter: string;
  minLoanAmount: string;
  maxLoanAmount: string;
}

const MIN_YEAR_RANGE = 10;

const SettlementAgeReportPage: React.FC = () => {
  const userDataResult = useUserData();
  const userData = userDataResult?.userData as any;
  const userLoading = userDataResult?.loading ?? true;
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkLoanId = searchParams.get('loanId');

  // Deep-link state
  const deepLinkRowRef = useRef<HTMLTableRowElement | null>(null);
  const deepLinkProcessed = useRef(false);
  const [highlightedLoanId, setHighlightedLoanId] = useState<string | null>(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);

  const { loanBooks = [] } = useOutletContext<{ loanBooks: ResolvedLoanBook[] }>();

  // Loan Book filter state.
  //
  // The option values are loan_book ids (companies/{companyId}/loan_books), which is
  // what resolveLoanBookSelection() resolves against. The previous version sourced
  // options from userVisibleTemplates (fileTemplate ids) and gated the picker on a
  // companyData.showTemplateFilter flag: under the unified loan-book model the
  // fileTemplate ids never matched an lb_* id, so a specific pick silently returned
  // full-book data. That was mitigated at the Entourage cutover by turning the flag
  // off for every tenant (docs/runbooks/2026-07-18-entourage-cutover.md), which left
  // the page with no Loan Book filter at all. Sourcing from loanBooks restores it and
  // removes the silent no-op; the flag is gone.
  const [selectedLoanBookId, setSelectedLoanBookId] = useState<string>('all');

  const resolvedParams = useMemo(() =>
      resolveLoanBookSelection(selectedLoanBookId, loanBooks),
      [selectedLoanBookId, loanBooks]
  );

  // Nothing to choose between when the tenant has a single book.
  const showLoanBookFilter = loanBooks.length > 1;

  // Filter state (moved above hook call so we can pass yearRange to hook)
  const [filters, setFilters] = useState<FilterState>({
    yearRangeStart: 0,
    yearRangeEnd: MIN_YEAR_RANGE,
    brokerFilter: '',
    lenderFilter: '',
    minLoanAmount: '',
    maxLoanAmount: ''
  });

  const [maxYears, setMaxYears] = useState<number>(MIN_YEAR_RANGE);
  const [brokers, setBrokers] = useState<string[]>([]);
  const [lenders, setLenders] = useState<string[]>([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState<string>('');
  const [insightsExpanded, setInsightsExpanded] = useState<boolean>(true);

  // UI state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Canonical loan-contact store (dual-reads settlement_contacts + canonical
  // loan_contacts under the hood — no history lost on the switch).
  const { contactsByLoan, mutateContact } = useLoanContacts(userData?.companyId);
  const [savingContact, setSavingContact] = useState<Record<string, boolean>>({});

  // Active company brokers for the contact "Made By" dropdown.
  // Broker / broker_assistant: locked to self. Other roles: all active brokers.
  const [contactBrokerList, setContactBrokerList] = useState<{ id: string; name: string }[]>([]);

  // ============================================
  // BIGQUERY DATA FETCHING (replaces Firestore onSnapshot)
  // ============================================
  const { loans: allLoans, loading, error, refetch } = useSettlementAge({
    companyId: userData?.companyId || '',
    brokerUid: userData?.role === 'broker' ? userData?.uid : undefined,
    templateId: resolvedParams.templateId,
    entityCode: resolvedParams.entityCode,
    // Note: Age filtering handled client-side (allows dynamic filter changes without refetch)
    enabled: !userLoading && !!userData?.companyId,
  });

  // Update bounds, broker list and lender list when loans change.
  //
  // This now re-runs on a Loan Book change too (that filter refetches), so the option
  // lists are rebuilt against the newly selected book. A broker or lender the caller
  // had selected may not exist in that book: leaving it set would show an empty
  // <select> filtering everything out, so a selection that is no longer offered is
  // cleared back to "All".
  useEffect(() => {
    // Extract unique brokers
    const uniqueBrokers = [...new Set(
      allLoans
        .map(l => l.broker)
        .filter((b): b is string => Boolean(b))
    )].sort();

    // Extract unique lenders. The backend resolves one canonical lender per loan
    // (alias variants like 'ANZ' / 'ANZ Australia' already collapsed), so each
    // lender appears exactly once here.
    const uniqueLenders = [...new Set(
      allLoans
        .map(l => l.lender)
        .filter((l): l is string => Boolean(l))
    )].sort();

    setBrokers(uniqueBrokers);
    setLenders(uniqueLenders);

    if (allLoans.length > 0) {
      // Update max years based on oldest loan
      const oldestAge = Math.ceil(allLoans[0].settlementAge);
      const newMaxYears = Math.max(MIN_YEAR_RANGE, oldestAge);
      setMaxYears(newMaxYears);

      setFilters(prev => ({
        ...prev,
        yearRangeEnd: newMaxYears,
        brokerFilter: prev.brokerFilter && !uniqueBrokers.includes(prev.brokerFilter)
          ? ''
          : prev.brokerFilter,
        lenderFilter: prev.lenderFilter && !uniqueLenders.includes(prev.lenderFilter)
          ? ''
          : prev.lenderFilter,
      }));
    } else {
      // Empty book: no option can still be valid.
      setFilters(prev => ({ ...prev, brokerFilter: '', lenderFilter: '' }));
    }
  }, [allLoans]);

  // Fetch active company brokers for the contact "Made By" dropdown.
  // Broker / broker_assistant: only the logged-in broker (skip Firestore read).
  // Other roles: all active brokers from Firestore.
  useEffect(() => {
    if (!userData?.companyId) return;

    if (userData?.role === 'broker' || userData?.role === 'broker_assistant') {
      const brokerName = userData.commissionDataName || userData.displayName || '';
      if (brokerName) {
        setContactBrokerList([{ id: 'self', name: brokerName }]);
      }
      return;
    }

    const brokersQuery = query(
      collection(db, `companies/${userData.companyId}/brokers`)
    );

    const unsubscribe = onSnapshot(
      brokersQuery,
      (snapshot) => {
        const brokers: { id: string; name: string }[] = [];
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.mergedInto) return;
          if ((data.status || 'active') !== 'active') return;
          if (data.currentName && typeof data.currentName === 'string') {
            brokers.push({ id: docSnap.id, name: data.currentName });
          }
        });
        brokers.sort((a, b) => a.name.localeCompare(b.name));
        setContactBrokerList(brokers);
      },
      (err) => {
        console.error('[SETTLEMENT_AGE] Error fetching brokers for contact dropdown:', err);
      }
    );

    return () => unsubscribe();
  }, [userData?.companyId, userData?.role, userData?.commissionDataName, userData?.displayName]);

  // Filtered loans with client-side filtering
  const filteredLoans = useMemo(() => {
    const filtered = allLoans.filter(loan => {
      // Age range filter
      if (loan.settlementAge < filters.yearRangeStart ||
          loan.settlementAge > filters.yearRangeEnd) {
        return false;
      }

      // Broker filter
      if (filters.brokerFilter && filters.brokerFilter !== '' && filters.brokerFilter !== 'All Brokers') {
        if (loan.broker !== filters.brokerFilter) {
          return false;
        }
      }

      // Lender filter
      if (filters.lenderFilter && filters.lenderFilter !== '') {
        if (loan.lender !== filters.lenderFilter) {
          return false;
        }
      }

      // Customer name search
      if (customerSearchTerm && customerSearchTerm.trim() !== '') {
        const searchLower = customerSearchTerm.toLowerCase().trim();
        const customerName = (loan.customerName || '').toLowerCase();
        if (!customerName.includes(searchLower)) {
          return false;
        }
      }

      // Loan amount filters
      const loanAmount = loan.loanAmount || 0;

      if (filters.minLoanAmount) {
        const min = parseFloat(filters.minLoanAmount);
        if (loanAmount < min) return false;
      }

      if (filters.maxLoanAmount) {
        const max = parseFloat(filters.maxLoanAmount);
        if (loanAmount > max) return false;
      }

      return true;
    });

    // Sort by age (oldest first)
    filtered.sort((a, b) => b.settlementAge - a.settlementAge);

    return filtered;
  }, [allLoans, filters, customerSearchTerm]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalLoans = filteredLoans.length;
    const totalLoanValue = filteredLoans.reduce((sum, loan) => sum + (loan.loanAmount || 0), 0);
    const avgAge = totalLoans > 0
      ? filteredLoans.reduce((sum, loan) => sum + loan.settlementAge, 0) / totalLoans
      : 0;

    // Count loans with contact records (derived from canonical loan_contacts,
    // keyed by bare loan_id — the canonical unified store's doc id).
    const contactedCount = filteredLoans.filter(loan => {
      const entries = contactsByLoan[String(loan.loanId)];
      return entries && entries.length > 0;
    }).length;
    const contactedPercent = totalLoans > 0 ? (contactedCount / totalLoans) * 100 : 0;

    // Count loans with multiple records combined
    const combinedLoans = filteredLoans.filter(loan => (loan.recordCount || 1) > 1).length;

    return {
      totalLoans,
      totalLoanValue,
      avgAge,
      contactedCount,
      contactedPercent,
      combinedLoans,
    };
  }, [filteredLoans, contactsByLoan]);

  // Deep-link: navigate to row, expand, and scroll into view
  useEffect(() => {
    if (!deepLinkLoanId || loading || filteredLoans.length === 0 || deepLinkProcessed.current) return;

    const targetIndex = filteredLoans.findIndex(r => r.loanFingerprint === deepLinkLoanId);
    if (targetIndex === -1) {
      setSearchParams({}, { replace: true });
      deepLinkProcessed.current = true;
      return;
    }

    setExpandedRows(prev => new Set(prev).add(deepLinkLoanId));
    setHighlightedLoanId(deepLinkLoanId);
    setSearchParams({}, { replace: true });
    deepLinkProcessed.current = true;
    setScrollTrigger(prev => prev + 1);
  }, [deepLinkLoanId, loading, filteredLoans, setSearchParams]);

  // Deep-link: scroll to target row after render
  useEffect(() => {
    if (scrollTrigger === 0) return;
    const timer = setTimeout(() => {
      deepLinkRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    return () => clearTimeout(timer);
  }, [scrollTrigger]);

  // Row expansion handler
  const toggleRow = useCallback((loanFingerprint: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanFingerprint)) {
        newSet.delete(loanFingerprint);
      } else {
        newSet.add(loanFingerprint);
      }
      return newSet;
    });
  }, []);

  // Persist contacts to the canonical loan_contacts store. The shared
  // <ContactTimeline> owns the add/edit/delete UX + draft state; this wrapper
  // just writes the resulting entry array and drives the per-loan saving spinner.
  const handleMutateContact = useCallback(async (input: MutateContactInput) => {
    setSavingContact(prev => ({ ...prev, [input.loanId]: true }));
    try {
      return await mutateContact(input);
    } finally {
      setSavingContact(prev => ({ ...prev, [input.loanId]: false }));
    }
  }, [mutateContact]);

  // Date formatter (helper function)
  const toDate = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (value?.toDate && typeof value.toDate === 'function') {
      return value.toDate();
    }
    if (value?.seconds) {
      return new Date(value.seconds * 1000);
    }
    return null;
  };

  // Formatters
  const formatDate = useCallback((dateValue: any): string => {
    const date = toDate(dateValue);
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  // Loading state
  if (userLoading) {
    return <SkeletonReportPage statCards={4} statColumns={4} tableColumns={4} />;
  }

  // Error state
  if (error && !loading && allLoans.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="bg-red-900/50 border border-red-500 text-red-200 p-8 rounded-lg max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4">Error Loading Data</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          icon={Clock}
          title="Settlement Age Report"
          subtitle="Track customer retention and outreach based on loan age"
          onRefresh={() => refetch()}
          isRefreshing={loading}
        />

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-8 mb-6">
          <div className="bg-gray-800 border border-gray-700/50 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Active Loans</div>
            <div className="text-3xl font-bold text-white">{summaryStats.totalLoans.toLocaleString()}</div>
          </div>
          <div className="bg-gray-800 border border-gray-700/50 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Total Funded Value</div>
            <div className="text-3xl font-bold text-white">{formatDollarsShorthand(summaryStats.totalLoanValue)}</div>
          </div>
          <div className="bg-gray-800 border border-gray-700/50 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Average Age</div>
            <div className="text-3xl font-bold text-white">{summaryStats.avgAge.toFixed(1)} years</div>
          </div>
          <div className="bg-gray-800 border border-gray-700/50 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Contacted</div>
            <div className="text-3xl font-bold text-white">{summaryStats.contactedPercent.toFixed(0)}%</div>
          </div>
        </div>

        {/* Insights Panel - Collapsible */}
        {filteredLoans.length > 0 && (
          <div className="mb-6 bg-gradient-to-r from-blue-900/50 to-indigo-900/50 border border-blue-500/30 rounded-xl overflow-hidden">
            <button
              onClick={() => setInsightsExpanded(!insightsExpanded)}
              className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            >
              <h3 className="text-lg font-bold text-white">Insights</h3>
              {insightsExpanded ? (
                <ChevronUp size={20} className="text-blue-300" />
              ) : (
                <ChevronDown size={20} className="text-blue-300" />
              )}
            </button>
            {insightsExpanded && (
              <div className="px-6 pb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-blue-300 font-medium mb-1">Outreach Coverage</div>
                    <div className="text-white">
                      {summaryStats.contactedCount} of {summaryStats.totalLoans} loans contacted ({summaryStats.contactedPercent.toFixed(1)}%)
                    </div>
                  </div>
                  <div>
                    <div className="text-blue-300 font-medium mb-1">Combined Records</div>
                    <div className="text-white">
                      {summaryStats.combinedLoans} loans have multiple commission records combined
                    </div>
                  </div>
                  <div>
                    <div className="text-blue-300 font-medium mb-1">Avg Funded Value</div>
                    <div className="text-white">
                      {formatDollars(summaryStats.totalLoans > 0 ? summaryStats.totalLoanValue / summaryStats.totalLoans : 0)}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-blue-200 text-xs">
                  <strong>Tip:</strong> Focus outreach on older loans (5+ years) that haven't been contacted. These customers may be due for refinancing or have new lending needs.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filter Controls
            Row 1: the two range filters (age, loan amount).
            Row 2: the four dimension filters (broker, loan book, lender, customer). */}
        <div className="bg-gray-800 p-6 rounded-xl shadow-lg mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Filter Options</h3>

          {/* Row 1: ranges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Years Ago (From)</label>
              <select
                value={filters.yearRangeStart}
                onChange={e => {
                  const value = Number(e.target.value);
                  setFilters(prev => ({
                    ...prev,
                    yearRangeStart: value,
                    yearRangeEnd: Math.max(value, prev.yearRangeEnd)
                  }));
                }}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: maxYears + 1 }, (_, y) => (
                  <option key={y} value={y}>{y} Year{y !== 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Years Ago (To)</label>
              <select
                value={filters.yearRangeEnd}
                onChange={e => setFilters(prev => ({ ...prev, yearRangeEnd: Number(e.target.value) }))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: (maxYears - filters.yearRangeStart) + 1 }, (_, i) => {
                  const y = filters.yearRangeStart + i;
                  return <option key={y} value={y}>{y} Year{y !== 1 ? 's' : ''}</option>;
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Min Loan Amount</label>
              <input
                type="number"
                value={filters.minLoanAmount}
                onChange={e => setFilters(prev => ({ ...prev, minLoanAmount: e.target.value }))}
                placeholder="$0"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Max Loan Amount</label>
              <input
                type="number"
                value={filters.maxLoanAmount}
                onChange={e => setFilters(prev => ({ ...prev, maxLoanAmount: e.target.value }))}
                placeholder="No limit"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Row 2: dimensions. Loan Book is omitted when the tenant has a single
              book (nothing to choose between). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Broker</label>
              <select
                value={filters.brokerFilter}
                onChange={e => setFilters(prev => ({ ...prev, brokerFilter: e.target.value }))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Brokers</option>
                {brokers.map(broker => (
                  <option key={broker} value={broker}>{broker}</option>
                ))}
              </select>
            </div>

            {showLoanBookFilter && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Loan Book</label>
                <select
                  value={selectedLoanBookId}
                  onChange={e => setSelectedLoanBookId(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Loan Books</option>
                  {loanBooks.map(lb => (
                    <option key={lb.id} value={lb.id}>
                      {lb.name}{lb.entityCode ? ` (${lb.entityCode})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Lender</label>
              <select
                value={filters.lenderFilter}
                onChange={e => setFilters(prev => ({ ...prev, lenderFilter: e.target.value }))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Lenders</option>
                {lenders.map(lender => (
                  <option key={lender} value={lender}>{lender}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Customer Name</label>
              <input
                type="text"
                placeholder="Search customer..."
                value={customerSearchTerm}
                onChange={e => setCustomerSearchTerm(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <p className="text-sm text-gray-400 mt-4">
            Showing {filteredLoans.length.toLocaleString()} of {allLoans.length.toLocaleString()} active loans
            ({filters.yearRangeStart} to {filters.yearRangeEnd} years old)
            {customerSearchTerm && ` • Filtered by: "${customerSearchTerm}"`}
          </p>
        </div>

        {/* Results Table */}
        {loading ? (
          <div className="flex justify-center items-center p-12">
            <Spinner size="lg" label="Loading loans..." />
          </div>
        ) : filteredLoans.length === 0 ? (
          <div className="bg-gray-800 p-12 rounded-xl shadow-lg text-center">
            <Clock size={48} className="mx-auto mb-4 opacity-50 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">No Active Loans Found</h3>
            <p className="text-gray-400">
              {allLoans.length === 0
                ? "No active loans found. Upload commission data to populate this report."
                : "Adjust filters to view active loans. All loans may be outside the selected range."}
            </p>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            <table className="w-full divide-y divide-gray-700">
              <thead className="bg-gray-900">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Customer</th>
                  <th scope="col" className="px-6 py-4 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Age</th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Loan Amount</th>
                  <th scope="col" className="px-6 py-4 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredLoans.map(loan => {
                  const isExpanded = expandedRows.has(loan.loanFingerprint);
                  // Contact identity = bare loan_id (canonical unified store key);
                  // row navigation/expansion stays on the composite loanFingerprint.
                  const contactKey = String(loan.loanId);
                  const isSaving = savingContact[contactKey];
                  const recordCount = loan.recordCount || 1;
                  const contactCount = (contactsByLoan[contactKey] || []).length;

                  return (
                    <React.Fragment key={loan.loanFingerprint}>
                      <tr
                        ref={highlightedLoanId === loan.loanFingerprint ? deepLinkRowRef : undefined}
                        className={`hover:bg-gray-700 transition-colors ${highlightedLoanId === loan.loanFingerprint ? 'ring-2 ring-blue-500/50 bg-blue-900/20' : ''}`}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-white">{loan.customerName || 'N/A'}</div>
                          <div className="text-xs text-gray-400">{loan.broker || 'Unknown'} • {formatDate(loan.settlementDate)}</div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-600 text-white">
                            {loan.settlementAge.toFixed(1)} yrs
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-sm text-gray-300">{formatDollars(loan.loanAmount || 0)}</div>
                          {contactCount > 0 && (
                            <div className="text-xs text-green-400 mt-0.5">{contactCount} contact{contactCount !== 1 ? 's' : ''}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => toggleRow(loan.loanFingerprint)}
                            className="inline-flex items-center gap-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm"
                          >
                            {isExpanded ? (
                              <>
                                <ChevronDown size={16} />
                                Hide
                              </>
                            ) : (
                              <>
                                <ChevronRight size={16} />
                                Details
                              </>
                            )}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={4} className="px-6 py-4 bg-gray-900/50">
                              <div className="bg-gray-900 p-4 rounded-lg">
                                {/* Combined Records Indicator */}
                                {recordCount > 1 && (
                                  <div className="mb-4 bg-purple-900/30 border border-purple-500/30 rounded-lg p-3 flex items-center gap-3">
                                    <Layers size={20} className="text-purple-400" />
                                    <div>
                                      <span className="text-purple-300 font-medium">{recordCount} commission records combined</span>
                                      <p className="text-purple-200/70 text-xs mt-0.5">
                                        This loan has multiple commission records (e.g., broker name changes, lender variations) that have been consolidated into a single entry.
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Canonical, cross-report contact history */}
                                <ContactTimeline
                                  companyId={userData?.companyId || ''}
                                  loanId={contactKey}
                                  customerName={loan.customerName}
                                  loanBroker={loan.broker}
                                  sourcePage="Settlement Age"
                                  entries={contactsByLoan[contactKey] || []}
                                  availableBrokers={contactBrokerList}
                                  currentUser={userData}
                                  onMutate={handleMutateContact}
                                  isSaving={isSaving}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettlementAgeReportPage;
